/**
 * i18n for Keisuke adapters (non-React). Uses the shared i18next instance.
 */
import i18n from '../i18n/config';

export function kt(key: string, options?: Record<string, unknown>): string {
  return String(i18n.t(`keisuke.adapter.${key}`, options ?? {}));
}

/** Adapter i18n for a specific manuscript / paper locale (independent of UI language). */
export function ktLng(
  lng: string,
  key: string,
  options?: Record<string, unknown>
): string {
  return String(i18n.getFixedT(lng)(`keisuke.adapter.${key}`, options ?? {}));
}

/** User-visible adapter / validation errors (keisuke.errors.*). */
export function ke(key: string, options?: Record<string, unknown>): string {
  return String(i18n.t(`keisuke.errors.${key}`, options ?? {}));
}

export type KeisukeErrorNamespace =
  | 'errors'
  | 'anova.errors'
  | 'doseResponse.errors'
  | 'survival.errors'
  | 'ttest.unpaired'
  | 'ttest.paired'
  | 'qpcr.errors'
  | 'scatterPlot.errors'
  | 'longitudinal.errors'
  | 'dataInput';

export interface KeisukeErrorOptions extends Record<string, unknown> {
  ns?: KeisukeErrorNamespace;
}

/** Resolve a Keisuke error message for any supported namespace. */
export function keNs(
  namespace: KeisukeErrorNamespace,
  key: string,
  options?: Record<string, unknown>
): string {
  return String(i18n.t(`keisuke.${namespace}.${key}`, options ?? {}));
}

export class KeisukeAdapterError extends Error {
  readonly code: string;
  readonly namespace: KeisukeErrorNamespace;

  constructor(code: string, options?: KeisukeErrorOptions) {
    const { ns = 'errors', ...i18nOpts } = options ?? {};
    const message = keNs(ns, code, i18nOpts);
    super(message);
    this.name = 'KeisukeAdapterError';
    this.code = code;
    this.namespace = ns;
  }
}

/** p-value for adapter stats sentences (`p < 0.001` / `p = 0.0123`). */
export function formatAdapterPValue(p: number): string {
  if (!Number.isFinite(p) || p < 0) return 'p = —';
  if (p < 0.001) return 'p < 0.001';
  if (p >= 0.01) return `p = ${p.toFixed(3)}`;
  return `p = ${p.toFixed(4)}`;
}
